/**
 * Sponsor Budget Import Dialog.
 *
 * Workflow:
 *  1. User uploads .xlsx
 *  2. Parser produces a ParsedBudget (header + visit grid + ad-hoc costs)
 *  3. Preview screen shows what will be persisted
 *  4. On confirm:
 *     - upload raw file to private Storage bucket `trial-budgets`
 *     - insert one `sponsor_trial_budgets` row (raw_payload, source_file_url)
 *     - insert one `protocol_fee_schedules` row per fee (with visit_quantities + overhead_pct)
 */
import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useQueryClient } from "@tanstack/react-query";
import { Upload, FileSpreadsheet, AlertCircle, Loader2 } from "lucide-react";
import { parseBudgetXlsx, type ParsedBudget, type FeeCategory } from "@/lib/budgetParser";

/**
 * Map parser categories to values allowed by the
 * `protocol_fee_schedules.fee_category` CHECK constraint.
 * Allowed in DB: visit | procedure | milestone | per_diem | lab | imaging | patient_stipend | other
 */
const DB_FEE_CATEGORY: Record<FeeCategory, string> = {
  procedure: "procedure",
  non_procedure: "other",
  patient_stipend: "patient_stipend",
  ad_hoc: "other",
};

interface Props {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  trialId: string;
  trialSiteId: string;
  sponsorOrganizationId?: string | null;
}

export function BudgetImportDialog({ open, onOpenChange, trialId, trialSiteId, sponsorOrganizationId }: Props) {
  const { toast } = useToast();
  const qc = useQueryClient();
  const [file, setFile] = useState<File | null>(null);
  const [parsed, setParsed] = useState<ParsedBudget | null>(null);
  const [parsing, setParsing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reset = () => { setFile(null); setParsed(null); setError(null); };

  async function handleFile(f: File) {
    setFile(f);
    setError(null);
    setParsing(true);
    try {
      const result = await parseBudgetXlsx(f);
      if (result.fees.length === 0) {
        setError("No fee rows detected. Is this a sponsor budget template?");
      }
      setParsed(result);
    } catch (e: any) {
      setError(e.message || "Failed to parse file");
      setParsed(null);
    } finally {
      setParsing(false);
    }
  }

  async function handleConfirm() {
    if (!parsed || !file) return;
    setSaving(true);
    try {
      const { data: userRes } = await supabase.auth.getUser();
      const userId = userRes.user?.id ?? null;

      // 1. Upload raw file to Storage
      const path = `${trialSiteId}/${trialId}/${Date.now()}-${file.name}`;
      const { error: upErr } = await supabase.storage
        .from("trial-budgets")
        .upload(path, file, { upsert: false, contentType: file.type });
      if (upErr) throw new Error(`Upload failed: ${upErr.message}`);

      // 2. Insert sponsor_trial_budgets row
      const total = parsed.fees.reduce(
        (s, f) => s + f.unitCost * Object.values(f.visitQuantities).reduce((a, b) => a + b, 0),
        0
      );
      const { data: budgetRow, error: bErr } = await supabase
        .from("sponsor_trial_budgets" as any)
        .insert({
          trial_id: trialId,
          trial_site_id: trialSiteId,
          sponsor_organization_id: sponsorOrganizationId ?? null,
          total_budget: total,
          currency: parsed.currency || "USD",
          source_file_url: path,
          protocol_version: parsed.protocolVersion,
          country: parsed.country,
          principal_investigator: parsed.principalInvestigator,
          per_visit_totals: parsed.perVisitTotals,
          imported_at: new Date().toISOString(),
          imported_by: userId,
          raw_payload: parsed as any,
        })
        .select()
        .single();
      if (bErr) throw new Error(`Budget save failed: ${bErr.message}`);

      // 3. Deactivate previous schedules for this trial (so triggers use the new ones)
      await supabase
        .from("protocol_fee_schedules")
        .update({ is_active: false })
        .eq("trial_id", trialId);

      // 4. Insert one fee schedule per parsed fee + ad-hoc lines
      const overheadPct = parsed.overheadPct ?? 0;
      const lines = [
        ...parsed.fees.map(f => ({
          trial_id: trialId,
          fee_category: DB_FEE_CATEGORY[f.category],
          fee_name: f.name,
          amount: f.unitCost,
          currency: parsed.currency || "USD",
          is_active: true,
          effective_from: new Date().toISOString().slice(0, 10),
          event_trigger_type: "visit_completion",
          visit_quantities: f.visitQuantities,
          overhead_pct: f.category === "patient_stipend" ? 0 : overheadPct,
          source_budget_id: (budgetRow as any).id,
          created_by: userId,
        })),
        ...parsed.adHoc.map(f => ({
          trial_id: trialId,
          fee_category: DB_FEE_CATEGORY.ad_hoc,
          fee_name: f.name,
          amount: f.amount,
          currency: parsed.currency || "USD",
          is_active: true,
          effective_from: new Date().toISOString().slice(0, 10),
          event_trigger_type: "manual_only",
          visit_quantities: {},
          overhead_pct: 0,
          source_budget_id: (budgetRow as any).id,
          created_by: userId,
        })),
      ];

      // chunked insert (RLS-aware single batch is fine for typical sizes ~50)
      const { error: fErr } = await supabase
        .from("protocol_fee_schedules" as any)
        .insert(lines);
      if (fErr) throw new Error(`Fee schedule save failed: ${fErr.message}`);

      toast({
        title: "Budget imported",
        description: `${parsed.fees.length} fees + ${parsed.adHoc.length} ad-hoc lines. Total ${parsed.currency} ${total.toFixed(2)}.`,
      });
      qc.invalidateQueries({ queryKey: ["protocol-fee-schedules"] });
      qc.invalidateQueries({ queryKey: ["sponsor-trial-budgets"] });
      onOpenChange(false);
      reset();
    } catch (e: any) {
      toast({ title: "Import failed", description: e.message, variant: "destructive" });
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) reset(); onOpenChange(o); }}>
      <DialogContent className="max-w-4xl max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileSpreadsheet className="h-5 w-5 text-primary" /> Import Sponsor Budget
          </DialogTitle>
          <DialogDescription>
            Upload the sponsor budget spreadsheet (.xlsx). We'll create one fee schedule entry per
            line and tag the patient-reimbursement row as a stipend.
          </DialogDescription>
        </DialogHeader>

        {!parsed && (
          <div className="border-2 border-dashed rounded-lg p-8 text-center space-y-3">
            <Upload className="h-10 w-10 mx-auto text-muted-foreground" />
            <Label htmlFor="budget-file" className="cursor-pointer text-primary underline">
              {file ? file.name : "Choose .xlsx file"}
            </Label>
            <Input
              id="budget-file"
              type="file"
              accept=".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              className="hidden"
              onChange={(e) => e.target.files?.[0] && handleFile(e.target.files[0])}
            />
            {parsing && <p className="text-sm text-muted-foreground flex items-center justify-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Parsing…</p>}
            {error && (
              <Alert variant="destructive" className="text-left">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}
          </div>
        )}

        {parsed && (
          <div className="flex-1 overflow-hidden flex flex-col gap-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
              <Field label="Protocol" value={parsed.protocolNumber} />
              <Field label="Currency" value={parsed.currency} />
              <Field label="Country" value={parsed.country} />
              <Field label="PI" value={parsed.principalInvestigator} />
              <Field label="Visit columns" value={String(parsed.visitColumns.length)} />
              <Field label="Procedure fees" value={String(parsed.fees.filter(f => f.category === "procedure").length)} />
              <Field label="Non-procedure fees" value={String(parsed.fees.filter(f => f.category === "non_procedure").length)} />
              <Field label="Patient stipend rows" value={String(parsed.fees.filter(f => f.category === "patient_stipend").length)} />
            </div>

            {error && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <ScrollArea className="flex-1 border rounded-md">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Category</TableHead>
                    <TableHead>Fee</TableHead>
                    <TableHead className="text-right">Unit cost</TableHead>
                    <TableHead className="text-right">Visits hit</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {parsed.fees.map((f, i) => (
                    <TableRow key={i}>
                      <TableCell>
                        <Badge variant={f.category === "patient_stipend" ? "default" : "outline"}>
                          {f.category.replace("_", " ")}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-xs">{f.name}</TableCell>
                      <TableCell className="text-right font-mono">{parsed.currency} {f.unitCost.toFixed(2)}</TableCell>
                      <TableCell className="text-right">{Object.keys(f.visitQuantities).length}</TableCell>
                    </TableRow>
                  ))}
                  {parsed.adHoc.map((f, i) => (
                    <TableRow key={`ah-${i}`}>
                      <TableCell><Badge variant="secondary">ad hoc</Badge></TableCell>
                      <TableCell className="text-xs">{f.name}</TableCell>
                      <TableCell className="text-right font-mono">{parsed.currency} {f.amount.toFixed(2)}</TableCell>
                      <TableCell className="text-right">—</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </ScrollArea>
          </div>
        )}

        <DialogFooter>
          {parsed && (
            <Button variant="ghost" onClick={reset} disabled={saving}>Choose different file</Button>
          )}
          <Button
            onClick={handleConfirm}
            disabled={!parsed || saving || parsing}
          >
            {saving ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Saving…</> : "Import & activate"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, value }: { label: string; value: string | null }) {
  return (
    <div className="bg-muted/40 rounded-md px-3 py-2">
      <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className="font-medium truncate">{value || "—"}</div>
    </div>
  );
}
